"use client";

import { CheckCircle2 } from "lucide-react";
import { ImageCarousel } from "@/components/features/about/image-carousel";
import { homeTheme } from "@/lib/theme/home-theme";
import { useLanguage } from "@/lib/i18n/context";
import { motion } from "framer-motion";

export function AboutSection() {
  const { t } = useLanguage();

  return (
    <section id="o-nas" className={homeTheme.aboutSection}>
      {/* Decorative ambient elements */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-64 w-64 rounded-full opacity-10 blur-[100px] pointer-events-none bg-violet-400 dark:bg-indigo-500"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-16 lg:items-center mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Section tag */}
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-orange-600 dark:text-orange-400 mb-4">
              {t.nav.about}
            </span>

            <h2 className="text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-5xl text-balance">
              {t.home.aboutTitle}
            </h2>
            <p className="mt-8 text-xl leading-relaxed text-zinc-600 dark:text-zinc-400">
              {t.home.aboutDesc}
            </p>
            <ul className="mt-12 max-w-xl space-y-6 text-base leading-7 text-zinc-600 dark:text-zinc-400 lg:max-w-none">
              {t.home.aboutList.map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="relative pl-10 flex items-center group list-none"
                >
                  <div
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-orange-100 dark:bg-orange-950/40 flex items-center justify-center text-orange-600 dark:text-orange-400 group-hover:scale-110 transition-transform shadow-sm"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <span className="text-lg font-medium text-zinc-900 dark:text-zinc-200 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative aspect-square md:aspect-[4/3] w-full rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-stone-200 dark:ring-zinc-700 p-2 md:p-3 bg-stone-100 dark:bg-zinc-800"
          >
            <div className="absolute inset-2 md:inset-3 rounded-[2rem] overflow-hidden">
              <ImageCarousel />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
